import {
    HttpClient,
    HttpEvent,
    HttpParams,
    HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PaginatedApiResponse } from '@wdx/clmi/api-services/models';
import {
    Document,
    DocumentRequirement,
    Regarding,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { ApiPaginationService } from '@wdx/shared/infrastructure/api-service';
import { Observable, map as rxMap } from 'rxjs';
import { PaginationOptions } from '../../models/pagination-options';
import { ConfigService } from '../../services/config.service';

@Injectable()
export class DocumentsService {
    private http = inject(HttpClient);
    private config = inject(ConfigService);
    private apiPaginationService = inject(ApiPaginationService);


    getDocumentsForClientProduct(
        paginationOptions: PaginationOptions,
        clientProductId: string,
    ): Observable<PaginatedApiResponse<Document>> {
        return this.http.get<PaginatedApiResponse<Document>>(
            `${
                this.config.getConfiguration().API_BASE
            }/clientproduct/${clientProductId}/document`,
            {
                headers:
                    this.apiPaginationService.mapPaginationOptions(
                        paginationOptions,
                    ),
            },
        );
    }

    getDocumentsForEntityId(
        entityType: SystemEntity,
        entityId: string,
    ): Observable<Document[]> {
        const isOpportunity = entityType === SystemEntity.Opportunity;
        const documentPath = isOpportunity ? 'document' : 'documents';
        let params = new HttpParams();
        // The document lookup is used for adding documents to an envelope, which should
        // only ever offer Active documents (e.g. not Superceded/Rejected/Expired).
        params = params.append('activeOnly', true);
        if (!isOpportunity) {
            params = params.append('includeRelated', true);
        }
        if (isOpportunity) {
            return this.http
                .get<PaginatedApiResponse<Document>>(
                    `${this.config.getConfiguration().API_BASE}/${String(
                        entityType,
                    ).toLowerCase()}/${entityId}/${documentPath}`,
                    { params },
                )
                .pipe(rxMap((response) => response.results ?? []));
        }
        return this.http.get<Document[]>(
            `${this.config.getConfiguration().API_BASE}/${String(
                entityType,
            ).toLowerCase()}/${entityId}/${documentPath}`,
            { params },
        );
    }

    deleteDocument(documentId: string): Observable<any> {
        return this.http.delete(
            `${this.config.getConfiguration().API_BASE}/document/${documentId}`,
            {},
        );
    }

    dissociateDocument(id: string, regarding: Regarding[]): Observable<any> {
        return this.http.patch(
            `${
                this.config.getConfiguration().API_BASE
            }/document/${id}/dissociate`,
            regarding || [],
            {},
        );
    }

    getDocumentRequirement(id: string): Observable<DocumentRequirement> {
        return this.http.get<DocumentRequirement>(
            `${
                this.config.getConfiguration().API_BASE
            }/documentrequirement/${id}`,
        );
    }

    getAssociatedDocuments(
        documentRequirementId: string,
    ): Observable<Document[]> {
        return this.http.get<Document[]>(
            `${
                this.config.getConfiguration().API_BASE
            }/documentrequirement/${documentRequirementId}/documents`,
        );
    }

    uploadFile(
        id: string,
        entityId: string,
        entityType: SystemEntity,
        file: File,
    ): Observable<HttpEvent<any>> {
        const formData = new FormData();
        formData.append(file.name, file);

        return this.http.request(
            new HttpRequest(
                'POST',
                `${
                    this.config.getConfiguration().API_BASE
                }/${entityType.toLowerCase()}/${entityId}/document`,
                formData,
            ),
        );
    }

}
